import { yupResolver } from '@hookform/resolvers/yup';
import { type FocusEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import * as Yup from 'yup';

import { Analytics } from 'analytics/Analytics';
import SecureIcon from 'assets/icons/base/secure-icon.svg';
import { formValidateAnalyticEvents } from 'constants/form-validate-analytic-events';
import {
  useIsMobile,
  usePayment,
  useGetCert,
  useSelectCard,
  useTranslate,
  useValidate,
  useAmount,
  useYM,
  useComment,
} from 'hooks';
import { useAppSelector } from 'store/hooks';
import { ecryptedWithJwkPublickKey, renderIcon } from 'utils';
import { SelectCard } from 'views/components/base/select-card';
import { Button, Input, OfferLink } from 'views/components/base/ui';
import { Checkbox } from 'views/components/base/ui/checkbox';

type CardFieldsType = {
  pan: string;
  validity?: string;
  cvv?: string;
  cardholder?: string;
};

const CardForm = () => {
  const { paymentData, acquiring, isPayment, fullAmount } = useAppSelector(
    (state) => state.payment,
  );
  const { cert } = useGetCert();
  const { fetchSendPayment, isLoading } = usePayment();
  const { trackOnClick } = useYM();
  const { isMobile } = useIsMobile();
  const { addCard, updateCardName, removeCard, cards, selectedCard, setSelectedCard } =
    useSelectCard();
  const [isSaveCard, setIsSaveCard] = useState(false);
  const [focusName, setFocusName] = useState<keyof CardFieldsType>('pan');
  const { currentAmountForPayment, isValidCurrentAmount } = useAmount();
  const { comment, isValidComment } = useComment();
  const { yupObjectCardValidate } = useValidate();

  const validationSchema = useMemo(() => {
    const isNewCardPayment = selectedCard.newCard && !!isPayment;
    const needsCardholder = !!isPayment && !selectedCard.name;

    return Yup.object({
      pan: selectedCard.newCard ? yupObjectCardValidate.cardNumber : Yup.string(),
      validity: isNewCardPayment ? yupObjectCardValidate.validity : Yup.string(),
      cvv: isNewCardPayment ? yupObjectCardValidate.cvv : Yup.string(),
      cardholder: needsCardholder ? yupObjectCardValidate.cardholder : Yup.string(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPayment, selectedCard]);

  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isValid },
    setFocus,
    watch,
    reset,
  } = useForm<CardFieldsType>({
    mode: 'onChange',
    defaultValues: {
      pan: '',
      cvv: '',
      validity: '',
      cardholder: '',
    },
    resolver: yupResolver(validationSchema) as Resolver<CardFieldsType>,
    context: { isPayment },
  });

  const { t } = useTranslate();

  const cardNumber = watch('pan');

  const onHandleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocusName(e.target.name as keyof CardFieldsType);
  };

  const onBlurAnalytics = (e: FocusEvent<HTMLInputElement>) => {
    const name = e.target.name as keyof CardFieldsType;

    if (errors[name]) {
      Analytics.sentEvent(formValidateAnalyticEvents[name].error);
    } else {
      Analytics.sentEvent(formValidateAnalyticEvents[name].success);
    }
  };

  const fields: (keyof CardFieldsType)[] = ['pan', 'validity', 'cvv'];

  const findIsNextInput = (nameInput: keyof CardFieldsType): keyof CardFieldsType => {
    if (!errors[nameInput] && dirtyFields[nameInput]) {
      const indexField = fields.findIndex((item) => item === nameInput);

      if (indexField !== -1) {
        return findIsNextInput(fields[indexField + 1]);
      }

      return fields[indexField + 1];
    }

    return nameInput;
  };

  useEffect(() => {
    const indexField = fields.findIndex((item) => item === focusName);

    const nextInput = fields[indexField + 1];

    if (nextInput && !errors[focusName] && dirtyFields[focusName]) {
      setFocus(findIsNextInput(nextInput));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.pan, errors.validity, errors.cvv]);

  useEffect(() => {
    trackOnClick('openCardForm');

    reset();
    setIsSaveCard(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard]);

  const sendPayment = async (data: CardFieldsType) => {
    if (cert) {
      const cardData = {
        ...data,
        pan: data.pan.replace(/\s/g, ''),
      };
      const crypt = await ecryptedWithJwkPublickKey(cert, JSON.stringify(cardData));

      const payData = {
        phone: '',
        email: '',
        description: comment,
        ID: paymentData?.ID,
        acquiringID: acquiring?.ID,
        amount: currentAmountForPayment,
        bankBin: cardData.pan ? cardData.pan.slice(0, 6) : null,
        cardData: crypt,
      };

      if (!selectedCard.newCard && selectedCard.crypt && selectedCard.bankBin) {
        payData.cardData = selectedCard.crypt;
        payData.bankBin = selectedCard.bankBin;
      }

      await fetchSendPayment(payData).then(() => {
        if (crypt && isSaveCard) {
          addCard({
            crypt,
            pan: cardData.pan,
            name: data.cardholder || undefined,
          });
        }

        if (!selectedCard.newCard && !selectedCard.name && data.cardholder) {
          updateCardName(selectedCard.id, data.cardholder);
        }
      });
    }
  };

  const renderBankOrTypeIcon = useCallback(() => {
    if (!selectedCard.newCard && selectedCard.bankBin) {
      return renderIcon(selectedCard.bankBin);
    }

    if (cardNumber) {
      return renderIcon(cardNumber);
    }

    return undefined;
  }, [cardNumber, selectedCard]);

  const handleSetIsSaveCard = () => {
    setIsSaveCard(!isSaveCard);
  };

  return (
    paymentData && (
      <form
        onSubmit={handleSubmit(sendPayment)}
        autoComplete="on"
        className="md:max-w-[366px] flex flex-col"
      >
        {cards.length > 0 && (
          <div>
            <SelectCard
              handleDelete={(pan) => removeCard(pan)}
              cards={cards}
              selectedCard={selectedCard}
              setSelectedCard={setSelectedCard}
            />
          </div>
        )}
        <div className="my-5 md:mt-[30px] md:mb-[30px] lg:flex gap-4 relative md:max-w-none">
          <Controller
            name="pan"
            control={control}
            render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
              <Input
                label={t('card_number')}
                placeholder="0000 0000 0000 0000"
                LeftIcon={isMobile ? renderBankOrTypeIcon() : null}
                mask=" "
                isMobile={!!isMobile}
                name="pan"
                autoComplete="cc-number"
                format={selectedCard.newCard ? '#### #### #### ####' : selectedCard.maskedPan}
                allowEmptyFormatting={!selectedCard.newCard}
                containerClassName="flex-shrink-0 md:max-w-[208px] w-full"
                value={value}
                ref={ref}
                onChange={onChange}
                onBlur={onBlurAnalytics}
                onFocus={onHandleFocus}
                type="tel"
                error={error?.message}
                disabled={!isValidCurrentAmount || !selectedCard.newCard}
              />
            )}
          />
          {isPayment ? (
            <div className="flex gap-6 lg:gap-4 mt-6 lg:mt-0 md:max-w-[230px]">
              <Controller
                name="validity"
                control={control}
                render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
                  <Input
                    label={t('expired_date')}
                    name="validity"
                    placeholder="MM/YY"
                    mask=" "
                    isMobile={!!isMobile}
                    format={selectedCard.newCard ? '##/##' : '**/**'}
                    value={value}
                    onChange={onChange}
                    onFocus={onHandleFocus}
                    allowEmptyFormatting={!selectedCard.newCard}
                    autoComplete="cc-exp"
                    inputClassName="w-[80px] md:w-full text-center md:text-left"
                    onBlur={onBlurAnalytics}
                    ref={ref}
                    type="tel"
                    error={error?.message}
                    disabled={!isValidCurrentAmount || !selectedCard.newCard}
                  />
                )}
              />
              <Controller
                name="cvv"
                control={control}
                render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
                  <Input
                    name="cvv"
                    label="CVV"
                    mask=""
                    autoComplete="cc-csc"
                    placeholder="000"
                    isMobile={!!isMobile}
                    format={selectedCard.newCard ? '###' : '***'}
                    value={value}
                    onChange={onChange}
                    onFocus={onHandleFocus}
                    onBlur={onBlurAnalytics}
                    ref={ref}
                    inputClassName={`w-[80px] md:w-[33px] text-center md:text-left${
                      selectedCard.newCard ? ' [-webkit-text-security:disc]' : ''
                    }`}
                    info={t('1xbet.form-bank-card.input.cvv.tooltip')}
                    type="tel"
                    allowEmptyFormatting={!selectedCard.newCard}
                    error={error?.message}
                    disabled={!isValidCurrentAmount || !selectedCard.newCard}
                  />
                )}
              />
            </div>
          ) : null}
          {!isMobile && (
            <div className="w-6 h-6 absolute -right-7 top-0 translate-y-5">
              {renderBankOrTypeIcon()}
            </div>
          )}
        </div>
        {isPayment && (
          <div className="mb-5 md:mb-[38px]">
            <Controller
              name="cardholder"
              control={control}
              render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
                <>
                  <Input
                    label={t('owner_name')}
                    name="cardholder"
                    autoComplete="cc-name"
                    placeholder={t('enter_owner_card_name')}
                    isMobile={!!isMobile}
                    value={!selectedCard.newCard && selectedCard.name ? selectedCard.name : value}
                    ref={ref}
                    maxLength={40}
                    disabled={!selectedCard.newCard && !!selectedCard.name}
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/[^a-zA-Z\s-]/g, '');
                      onChange(e);
                    }}
                    error={!!error}
                  />
                  <p
                    className={`mt-1 text-[10px] leading-3 ${error ? 'text-base-red-100 lowercase' : 'text-base-gray-600/50'}`}
                  >
                    {error ? error.message : t('cardholder_hint')}
                  </p>
                </>
              )}
            />
          </div>
        )}
        <div className="text-[10px] font-normal leading-3 text-dark/60 flex items-center gap-2">
          <img src={SecureIcon} width={18} height={18} alt="secure" />
          {t('lang.card_data_encrypted')}
        </div>
        {selectedCard.newCard && (
          <div className="mt-3 md:mt-[30px]">
            <Checkbox
              name="save-card"
              label={t('save_card_checkbox')}
              checked={isSaveCard}
              onChange={handleSetIsSaveCard}
            />
          </div>
        )}
        <div className="mt-auto md:mt-6">
          <Button
            isLoading={isLoading}
            className="mb-2 md:max-w-[224px]"
            disabled={!isValid || !isValidCurrentAmount || !isValidComment}
            onClick={handleSubmit(sendPayment)}
          >
            {isPayment ? t('pay') : t('pay_out')}{' '}
            {isMobile && fullAmount && isValid ? `${fullAmount} KZT` : null}
          </Button>
          <OfferLink />
        </div>
      </form>
    )
  );
};

export { CardForm };
